import { environment } from '@/config/environment'

/**
 * Logger utility for consistent logging across the application.
 *
 * Features:
 * - Environment-aware logging (debug/info only in development)
 * - Configurable log levels
 * - Prefix support for module identification
 * - Child logger creation for sub-modules
 *
 * Usage examples:
 *
 * // Basic usage
 * import { logger } from '@/utils'
 * logger.info('User logged in', { userId: '123' })
 *
 * // Create a logger for a specific module
 * import { createLogger } from '@/utils'
 * const apiLogger = createLogger('API')
 * apiLogger.debug('Request sent', { url: '/api/users' })
 *
 * // Create a child logger
 * const userLogger = apiLogger.child('Users')
 * userLogger.info('User created', { userId: '456' })
 *
 * // Global configuration
 * import { configureLogger } from '@/utils'
 * configureLogger({ enableDebug: true, prefix: 'App' })
 */

export interface Logger {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

export interface LoggerConfig {
  enableDebug?: boolean
  enableInfo?: boolean
  enableWarn?: boolean
  enableError?: boolean
  prefix?: string
}

class AppLogger implements Logger {
  private config: LoggerConfig
  private prefix: string

  constructor(config: LoggerConfig = {}) {
    // Use getters for environment values to ensure lazy evaluation
    this.config = {
      enableDebug: config.enableDebug,
      enableInfo: config.enableInfo,
      enableWarn: config.enableWarn ?? true,
      enableError: config.enableError ?? true,
      prefix: config.prefix ?? '',
    }
    this.prefix = this.config.prefix ? `[${this.config.prefix}] ` : ''
  }

  debug = (...args: unknown[]): void => {
    const enableDebug = this.config.enableDebug ?? environment.isDevelopment
    if (enableDebug) {
      console.debug(this.prefix, ...args)
    }
  }

  info = (...args: unknown[]): void => {
    const enableInfo = this.config.enableInfo ?? environment.isDevelopment
    if (enableInfo) {
      console.info(this.prefix, ...args)
    }
  }

  warn = (...args: unknown[]): void => {
    if (this.config.enableWarn) {
      console.warn(this.prefix, ...args)
    }
  }

  error = (...args: unknown[]): void => {
    if (this.config.enableError) {
      console.error(this.prefix, ...args)
    }
  }

  // Method to update configuration at runtime
  updateConfig(newConfig: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...newConfig }
    this.prefix = this.config.prefix ? `[${this.config.prefix}] ` : ''
  }

  // Method to create a child logger with a prefix
  child(prefix: string): AppLogger {
    return new AppLogger({
      ...this.config,
      prefix: this.config.prefix ? `${this.config.prefix}.${prefix}` : prefix,
    })
  }
}

// Default logger instance
export const logger = new AppLogger()

// Factory function to create loggers for specific modules
export const createLogger = (prefix?: string, config?: LoggerConfig): AppLogger => {
  return new AppLogger({ ...config, prefix })
}

// Convenience function to set global logger configuration
export const configureLogger = (config: Partial<LoggerConfig>): void => {
  if (Object.keys(config).length === 0) {
    logger.updateConfig({
      enableDebug: undefined, // Will use environment.isDevelopment lazily
      enableInfo: undefined, // Will use environment.isDevelopment lazily
      enableWarn: true,
      enableError: true,
      prefix: '',
    })
    return
  }
  logger.updateConfig(config)
}

// Export the default logger methods for backward compatibility
export const debug = logger.debug.bind(logger)
export const info = logger.info.bind(logger)
export const warn = logger.warn.bind(logger)
export const error = logger.error.bind(logger)
